/**
 * InstanceAuditorDialog — Reassign an instance to a substitute auditor.
 *
 * Used for vacation coverage: supervisor reassigns a single frozen instance
 * to a different auditor without touching the schedule definition.
 *
 * The unique constraint (schedule_id, auditor_id, period_start) means
 * the same schedule + period can have different auditors — this is by design.
 */

import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLanguage } from '@/core/LanguageContext';
import { useFactory } from '@/core/hooks/useFactory';
import { queryKeys } from '@/core/lib/queryKeys';
import { fetchFactoryUsers } from '@/core/api/user-roles';
import { useUpdateInstanceAuditor } from '../hooks/useScheduleInstances';
import type { LpaScheduleInstanceWithDetails } from '../types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Avatar, AvatarFallback } from '@/shared/ui/avatar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/command';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/ui/tooltip';
import {
  Loader2,
  ChevronsUpDown,
  Check,
  HelpCircle,
  ArrowRight,
} from 'lucide-react';
import { cn } from '@/shared/lib/utils';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getInitials(name: string | null, email: string): string {
  if (name) {
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  return email.charAt(0).toUpperCase();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface InstanceAuditorDialogProps {
  instance: LpaScheduleInstanceWithDetails | null;
  onClose: () => void;
  auditId: string;
}

export default function InstanceAuditorDialog({
  instance,
  onClose,
  auditId,
}: InstanceAuditorDialogProps) {
  const { t } = useLanguage();
  const { selectedFactoryId } = useFactory();
  const updateAuditor = useUpdateInstanceAuditor();

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Fetch factory users
  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: queryKeys.factoryUsers(selectedFactoryId),
    queryFn: () => fetchFactoryUsers(selectedFactoryId!),
    enabled: !!selectedFactoryId && !!instance,
  });

  // Approved users, deduplicated
  const approvedUsers = useMemo(() => {
    const seen = new Set<string>();
    return users
      .filter((u) => {
        if (u.status !== 'approved') return false;
        if (seen.has(u.user_id)) return false;
        seen.add(u.user_id);
        return true;
      })
      .map((u) => ({
        id: u.user_id,
        full_name: (u.profile as { full_name?: string | null })?.full_name ?? null,
        email: (u.profile as { email?: string })?.email ?? '',
        role: u.role,
      }));
  }, [users]);

  // Reset when dialog opens
  useEffect(() => {
    if (instance) {
      setSelectedUserId(null);
    }
  }, [instance]);

  const selectedUser = approvedUsers.find((u) => u.id === selectedUserId);
  const currentAuditor = instance?.auditor;

  const handleSubmit = () => {
    if (!instance || !selectedUserId) return;
    updateAuditor.mutate(
      {
        instanceId: instance.id,
        auditId,
        scheduleId: instance.schedule_id,
        input: { auditor_id: selectedUserId },
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <Dialog open={!!instance} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>
              {t('mod_lpa.instances.auditorDialog.title', 'Reassign Auditor')}
            </DialogTitle>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="h-5 w-5">
                  <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent side="right" className="w-72 text-xs">
                {t(
                  'mod_lpa.instances.auditorDialog.help',
                  'Reassigning changes only this single instance — the schedule\'s default auditor remains unchanged. Use this for vacation coverage or one-time substitutions.',
                )}
              </PopoverContent>
            </Popover>
          </div>
          <DialogDescription>
            {t(
              'mod_lpa.instances.auditorDialog.description',
              'Reassign this instance to a substitute auditor. The schedule default remains unchanged.',
            )}
          </DialogDescription>
        </DialogHeader>

        {instance && (
          <div className="space-y-4 py-2">
            {/* Instance info */}
            <div className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">
                {instance.schedule.name}
              </span>
              {' — '}
              {new Date(instance.period_start).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
              })}
              {'–'}
              {new Date(instance.period_end).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
              })}
            </div>

            {/* Current → New visual */}
            <div className="flex items-center gap-3">
              {/* Current auditor */}
              <div className="flex items-center gap-2 flex-1 rounded-lg border p-2.5 bg-muted/30">
                <Avatar className="h-7 w-7">
                  <AvatarFallback className="text-xs">
                    {currentAuditor
                      ? getInitials(currentAuditor.full_name, currentAuditor.email)
                      : '?'}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">
                    {t('mod_lpa.instances.auditorDialog.current', 'Current')}
                  </p>
                  <p className="text-sm font-medium truncate">
                    {currentAuditor?.full_name ?? currentAuditor?.email ?? '?'}
                  </p>
                </div>
              </div>

              <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />

              {/* New auditor */}
              <div className="flex items-center gap-2 flex-1 rounded-lg border p-2.5 bg-primary/5 border-primary/20">
                <Avatar className="h-7 w-7">
                  <AvatarFallback className="text-xs">
                    {selectedUser
                      ? getInitials(selectedUser.full_name, selectedUser.email)
                      : '?'}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">
                    {t('mod_lpa.instances.auditorDialog.new', 'New')}
                  </p>
                  <p className="text-sm font-medium truncate">
                    {selectedUser
                      ? (selectedUser.full_name ?? selectedUser.email)
                      : t('mod_lpa.instances.auditorDialog.selectPlaceholder', 'Select...')}
                  </p>
                </div>
              </div>
            </div>

            {/* User picker */}
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={pickerOpen}
                  className="w-full justify-between font-normal"
                  disabled={usersLoading}
                >
                  {usersLoading ? (
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('mod_lpa.instances.auditorDialog.loading', 'Loading users...')}
                    </span>
                  ) : selectedUser ? (
                    <span className="flex items-center gap-2">
                      <Avatar className="h-5 w-5">
                        <AvatarFallback className="text-[10px]">
                          {getInitials(selectedUser.full_name, selectedUser.email)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="truncate">
                        {selectedUser.full_name ?? selectedUser.email}
                      </span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground">
                      {t('mod_lpa.instances.auditorDialog.selectAuditor', 'Select substitute auditor...')}
                    </span>
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput
                    placeholder={t('mod_lpa.instances.auditorDialog.search', 'Search by name or email...')}
                  />
                  <CommandList>
                    <CommandEmpty>
                      {t('mod_lpa.instances.auditorDialog.noUsers', 'No users found.')}
                    </CommandEmpty>
                    <CommandGroup>
                      {approvedUsers
                        .filter((u) => u.id !== instance.auditor_id) // Exclude current auditor
                        .map((user) => (
                          <CommandItem
                            key={user.id}
                            value={`${user.full_name ?? ''} ${user.email}`}
                            onSelect={() => {
                              setSelectedUserId(user.id);
                              setPickerOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                selectedUserId === user.id ? 'opacity-100' : 'opacity-0',
                              )}
                            />
                            <Avatar className="h-6 w-6 mr-2">
                              <AvatarFallback className="text-[10px]">
                                {getInitials(user.full_name, user.email)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col min-w-0">
                              <span className="text-sm truncate">
                                {user.full_name ?? user.email}
                              </span>
                              {user.full_name && (
                                <span className="text-xs text-muted-foreground truncate">
                                  {user.email}
                                </span>
                              )}
                            </div>
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="ml-auto text-[10px] text-muted-foreground capitalize">
                                    {user.role}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent className="text-xs">
                                  {t('mod_lpa.instances.auditorDialog.roleLabel', 'Factory role')}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </CommandItem>
                        ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={updateAuditor.isPending}>
            {t('mod_lpa.instances.auditorDialog.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedUserId || updateAuditor.isPending}
          >
            {updateAuditor.isPending && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            {t('mod_lpa.instances.auditorDialog.confirm', 'Reassign')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
